import { useState, useEffect } from "react";
import { api, errMsg, fmtMoney } from "../../lib/api";
import { STAGES, SERVICES, serviceName } from "../../lib/constants";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Rocket, Trash2, Phone, Mail } from "lucide-react";

export default function CRM() {
  const [leads, setLeads] = useState([]);
  const [dragged, setDragged] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", service_type: "moving", value: 0, notes: "" });

  const load = () => api.get("/leads").then((r) => setLeads(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const moveStage = async (leadId, stage) => {
    setLeads((ls) => ls.map((l) => (l.id === leadId ? { ...l, stage } : l)));
    try {
      await api.patch(`/leads/${leadId}`, { stage });
    } catch (e) {
      toast.error(errMsg(e));
      load();
    }
  };

  const onDrop = (e, stage) => {
    e.preventDefault();
    if (dragged) moveStage(dragged, stage);
    setDragged(null);
  };

  const createLead = async () => {
    try {
      await api.post("/leads", { ...form, value: Number(form.value) || 0 });
      toast.success("Lead created");
      setCreateOpen(false);
      setForm({ name: "", email: "", phone: "", service_type: "moving", value: 0, notes: "" });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const convert = async (leadId) => {
    try {
      const { data } = await api.post(`/leads/${leadId}/convert`);
      toast.success(`Mission ${data.mission_number} created`);
      setDetail(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async (leadId) => {
    await api.delete(`/leads/${leadId}`);
    toast.success("Lead removed");
    setDetail(null);
    load();
  };

  return (
    <div data-testid="ops-crm-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <p className="overline-cc">CRM</p>
          <h1 className="page-title mt-1.5">Pipeline</h1>
        </div>
        <Button data-testid="crm-new-lead-button" onClick={() => setCreateOpen(true)} className="rounded-full btn-glow bg-[var(--cc-accent)] text-[var(--cc-accent-fg)] hover:bg-[var(--cc-accent)]/90 font-semibold">
          <Plus className="w-4 h-4 mr-1" /> New Lead
        </Button>
      </div>

      <div className="flex gap-4 overflow-x-auto pb-4 kanban-scroll" data-testid="crm-kanban-board">
        {STAGES.map((stage) => {
          const items = leads.filter((l) => l.stage === stage.id);
          return (
            <div key={stage.id} data-testid={`crm-column-${stage.id}`}
              onDragOver={(e) => e.preventDefault()} onDrop={(e) => onDrop(e, stage.id)}
              className="w-64 shrink-0 rounded-xl bg-secondary/50 border">
              <div className="px-4 py-3 border-b flex items-center justify-between">
                <p className="font-mono-cc text-[11px] tracking-widest uppercase font-semibold">{stage.name}</p>
                <span className="text-xs font-mono-cc text-muted-foreground">{items.length}</span>
              </div>
              <div className="p-2.5 space-y-2.5 min-h-[120px] max-h-[calc(100vh-280px)] overflow-y-auto">
                {items.map((l) => (
                  <div key={l.id} draggable data-testid={`crm-lead-card-${l.id}`}
                    onDragStart={() => setDragged(l.id)} onClick={() => setDetail(l)}
                    className="rounded-lg border bg-card p-3.5 cursor-grab active:cursor-grabbing card-lift">
                    <p className="text-sm font-semibold">{l.name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{serviceName(l.service_type)}</p>
                    <div className="flex items-center justify-between mt-2.5">
                      <span className="font-mono-cc text-xs text-[var(--cc-accent)]">{l.value ? fmtMoney(l.value) : "—"}</span>
                      <span className="text-[10px] text-muted-foreground font-mono-cc uppercase">{l.source?.slice(0, 12)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="crm-create-dialog">
          <DialogHeader><DialogTitle className="font-heading">New Lead</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Name</Label><Input data-testid="crm-lead-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Email</Label><Input data-testid="crm-lead-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Service</Label>
                <Select value={form.service_type} onValueChange={(v) => setForm({ ...form, service_type: v })}>
                  <SelectTrigger data-testid="crm-lead-service-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{SERVICES.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Est. value ($)</Label><Input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <Button data-testid="crm-create-submit-button" onClick={createLead} disabled={!form.name} className="w-full rounded-full btn-glow bg-[var(--cc-accent)] text-[var(--cc-accent-fg)] hover:bg-[var(--cc-accent)]/90 font-semibold">Create Lead</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="crm-lead-detail-dialog">
          {detail && (
            <>
              <DialogHeader><DialogTitle className="font-heading">{detail.name}</DialogTitle></DialogHeader>
              <div className="space-y-4 text-sm">
                <div className="flex flex-wrap gap-4 text-muted-foreground">
                  {detail.email && <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {detail.email}</span>}
                  {detail.phone && <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> {detail.phone}</span>}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg border p-3"><p className="overline-cc">Service</p><p className="mt-1 font-semibold">{serviceName(detail.service_type)}</p></div>
                  <div className="rounded-lg border p-3"><p className="overline-cc">Value</p><p className="mt-1 font-semibold">{detail.value ? fmtMoney(detail.value) : "TBD"}</p></div>
                </div>
                {(detail.pickup_address || detail.dropoff_address) && (
                  <p className="text-muted-foreground">{detail.pickup_address}{detail.dropoff_address ? ` → ${detail.dropoff_address}` : ""}</p>
                )}
                {detail.notes && <p className="text-muted-foreground italic">"{detail.notes}"</p>}
                <div className="space-y-1.5">
                  <Label>Stage</Label>
                  <Select value={detail.stage} onValueChange={(v) => { moveStage(detail.id, v); setDetail({ ...detail, stage: v }); }}>
                    <SelectTrigger data-testid="crm-detail-stage-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{STAGES.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="flex gap-3 pt-2">
                  {!detail.mission_id && (
                    <Button data-testid="crm-convert-button" onClick={() => convert(detail.id)} className="flex-1 rounded-full btn-glow bg-[var(--cc-accent)] text-[var(--cc-accent-fg)] hover:bg-[var(--cc-accent)]/90 font-semibold">
                      <Rocket className="w-4 h-4 mr-2" /> Convert to Mission
                    </Button>
                  )}
                  <Button data-testid="crm-delete-button" variant="outline" onClick={() => remove(detail.id)} className="rounded-full text-red-500"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
